import type { CareersLang } from "./careers-constants";

export const CAREERS_BENEFIT_ICON_KEYS = [
  "house",
  "clock",
  "shuffle",
  "userCheck",
  "sun",
  "graduationCap",
  "monitor",
  "armchair",
  "coffee",
  "cookie",
  "sparkles",
  "trainFront",
  "store",
  "utensils",
] as const;

export type CareersBenefitIconKey = (typeof CAREERS_BENEFIT_ICON_KEYS)[number];

export interface CareersBenefitItem {
  readonly icon: CareersBenefitIconKey;
  readonly title: Record<CareersLang, string>;
  readonly body: Record<CareersLang, string>;
}

export const CAREERS_BENEFITS_TITLE: Record<CareersLang, string> = {
  de: "Was wir dir bieten",
  en: "What we offer",
};

/** Classic 3-column benefit tiles: icon, headline, short body. */
export const CAREERS_BENEFITS: readonly CareersBenefitItem[] = [
  {
    icon: "house",
    title: { de: "2 Tage Homeoffice", en: "2 days home office" },
    body: {
      de: "Zwei Tage pro Woche von zu Hause. Der Rest im Team vor Ort. Unsere Softwareentwickler-Stellen sind 100% Remote.",
      en: "Two days a week from home. The rest on-site with the team. Software engineering roles are 100% remote.",
    },
  },
  {
    icon: "clock",
    title: { de: "Kernzeit 9 bis 15 Uhr", en: "Core hours 9 to 15" },
    body: {
      de: "In der Kernzeit erreichbar. Drumherum teilst du dir den Tag selbst ein.",
      en: "Available during core hours. Around that, you structure your own day.",
    },
  },
  {
    icon: "shuffle",
    title: { de: "Abwechslungsreiche Aufgaben", en: "Varied work" },
    body: {
      de: "Deine Aufgaben wechseln mit dem, was gerade ansteht.",
      en: "Your work changes with what is needed.",
    },
  },
  {
    icon: "userCheck",
    title: { de: "Eigenverantwortlich arbeiten", en: "Own your work" },
    body: {
      de: "Du arbeitest eigenverantwortlich. Was zählt, ist das Ergebnis.",
      en: "You work autonomously. What counts is the result.",
    },
  },
  {
    icon: "sun",
    title: { de: "Schöne Räume", en: "A place you like" },
    body: {
      de: "Helle Räume, ein lichtdurchflutetes Foyer und ein Ort, an dem du dich wohlfühlst.",
      en: "Bright rooms, a light-filled foyer, and a place you enjoy coming to.",
    },
  },
  {
    icon: "graduationCap",
    title: { de: "Weiterbildung", en: "Training" },
    body: {
      de: "Exzellente Weiterbildungsmöglichkeiten, wenn du dich weiterentwickeln willst.",
      en: "Strong training options if you want to keep growing.",
    },
  },
  {
    icon: "monitor",
    title: { de: "Moderne Technik", en: "First-class setup" },
    body: {
      de: "Moderner Arbeitsplatz mit erstklassiger technischer Ausstattung.",
      en: "A modern desk with first-class technical equipment.",
    },
  },
  {
    icon: "armchair",
    title: { de: "Ergonomischer Arbeitsplatz", en: "Ergonomic workplace" },
    body: {
      de: "Höhenverstellbarer Schreibtisch und ergonomische Stühle.",
      en: "Height-adjustable desk and ergonomic chairs.",
    },
  },
  {
    icon: "coffee",
    title: { de: "Kaffee & Getränke", en: "Coffee and drinks" },
    body: {
      de: "Kalt- und Warmgetränke und eine erstklassige Kaffeemaschine.",
      en: "Hot and cold drinks, plus a first-class coffee machine.",
    },
  },
  {
    icon: "cookie",
    title: { de: "Snacks nach Wahl", en: "Snacks you choose" },
    body: {
      de: "Einmal im Monat frei bestellen, was das Team mag.",
      en: "Once a month the team orders snacks it actually wants.",
    },
  },
  {
    icon: "sparkles",
    title: { de: "KI-Nutzung bezahlt", en: "AI paid for" },
    body: {
      de: "KI ist erwünscht und Kosten trägt das Unternehmen. Für Entwickler gilt das unbegrenzt.",
      en: "AI is encouraged and the company covers the cost. For software engineers that is unlimited.",
    },
  },
  {
    icon: "trainFront",
    title: { de: "Gute Anbindung", en: "Easy to reach" },
    body: {
      de: "Gute Verkehrsanbindung. Der Weg zur Arbeit bleibt kurz.",
      en: "Good public-transport connections. The commute stays short.",
    },
  },
  {
    icon: "store",
    title: { de: "Alles um die Ecke", en: "Everything next door" },
    body: {
      de: "Supermarkt, Bäcker und Restaurants fußläufig in unter einer Minute.",
      en: "Supermarket, bakery, and restaurants less than a minute away.",
    },
  },
  {
    icon: "utensils",
    title: { de: "Küche & Pausenraum", en: "Kitchen and break room" },
    body: {
      de: "Mikrowelle, Herd und Kühlschrank. Alles, was man braucht.",
      en: "Microwave, stove, and fridge. Everything you need.",
    },
  },
];
